/**
 * Locale-aware title-casing for category/destination labels.
 *
 * INTL bug-batch 2026-06-10 item 3: the old per-callsite
 * `.replace(/\b\w/g, c => c.toUpperCase())` treated accented letters as
 * non-word characters, so a word boundary fired INSIDE the word and the
 * letter after the accent got uppercased: "Städtetrips" → "StäDtetrips"
 * (.de), "dégustations" → "DéGustations", "bien être" → "Bien êTre"
 * (.fr). Six components each had their own copy of that regex; they all
 * route through here now.
 *
 * Rules:
 *  - slugs may use "-" or "_" as separators; both become spaces (the old
 *    callsites did `.split("-").join(" ")` / `.replace(/_/g, " ")`);
 *  - a value that already contains an uppercase letter is presentation
 *    copy from the CMS/i18n catalog ("Manger, boire et dégustations",
 *    "Städtetrips") - return it as-is, never re-case curated copy;
 *  - all-lowercase values (raw slugs like "city_trip", "zomervakantie")
 *    get cased per locale: de/fr use sentence case (French/German
 *    conventions don't capitalize every word), nl/en capitalize each
 *    space-separated word. Uppercasing uses toLocaleUpperCase so locale
 *    digraph rules apply.
 */
const BCP47_BY_LOCALE = { nl: "nl-NL", pl: "pl-PL", de: "de-DE", fr: "fr-FR", en: "en-GB" };

export function titleCaseLabel(value, locale = "nl") {
  if (!value || typeof value !== "string") return "";

  // Already presentation-cased? Leave it alone - including its hyphens
  // ("Bien-être" must stay "Bien-être", not become "Bien être").
  if (/\p{Lu}/u.test(value)) return value.trim();

  const spaced = value.replace(/[-_]+/g, " ").replace(/\s+/g, " ").trim();
  if (!spaced) return "";

  const tag = BCP47_BY_LOCALE[locale] || "nl-NL";
  const upperFirst = (word) =>
    word.charAt(0).toLocaleUpperCase(tag) + word.slice(1);

  if (locale === "de" || locale === "fr") {
    // Sentence case: only the very first letter.
    return upperFirst(spaced);
  }
  return spaced
    .split(" ")
    .map((word) => (word ? upperFirst(word) : word))
    .join(" ");
}
